import { Component, computed, DestroyRef, effect, inject, input, output } from "@angular/core";
import { takeUntilDestroyed, toObservable } from "@angular/core/rxjs-interop";
import { MonitoringCategory, MonitoringProductService } from "@domain/product/monitoring/monitoring-product.service";
import { ProductImage } from "@domain/product/product-image";
import { ProductService } from "@domain/product/product.service";
import { KvmConfiguration } from "@domain/project/configurations/monitoring/kvm-configuration";
import { FloorplanItem } from "@domain/project/floorplan/floorplan-item";
import { FloorplanSidesheetExtent } from "@domain/project/floorplan/floorplan-sidesheet-extent";
import { FloorplanKvm } from "@domain/project/floorplan/monitoring/floorplan-kvm";
import {
  buildKvmGroupedSourceItems,
  KvmGroupedPartLabels,
} from "@pages/project/monitoring/grouped-parts/monitoring-grouped-parts-source-item.factory";
import {
  getKvmGroupedPartLabels,
  getMonitoringConfigurationSummaryTitle,
} from "@pages/project/monitoring/grouped-parts/monitoring-grouped-parts.helper";
import { MonitoringGroupedPartSourceItem } from "@pages/project/monitoring/grouped-parts/monitoring-grouped-parts.types";
import { FloorplanItemDetailsComponent } from "@project/floorplanner/floorplan-additional-notes/floorplan-item-details/floorplan-item-details.component";
import { FloorplanCustomPartNumbersComponent } from "@project/floorplanner/floorplan-custom-part-numbers/floorplan-custom-part-numbers.component";
import { FloorplanItemAttachmentsButtonComponent } from "@project/floorplanner/floorplan-item-attachments-button/floorplan-item-attachments-button.component";
import { FloorplanItemGroupComponent } from "@project/floorplanner/floorplan-item-group/floorplan-item-group.component";
import { MonitoringProductItem } from "@project/monitoring-product-item";
import { catchError, map, of, switchMap } from "rxjs";

@Component({
  selector: "app-kvm-notes",
  imports: [
    FloorplanItemDetailsComponent,
    FloorplanItemGroupComponent,
    FloorplanItemAttachmentsButtonComponent,
    FloorplanCustomPartNumbersComponent,
  ],
  templateUrl: "./kvm-notes.component.html",
  styleUrl: "./kvm-notes.component.scss",
  standalone: true,
})
export class KvmNotesComponent {
  readonly floorplanItem = input.required<FloorplanItem>();
  readonly extent = input.required<FloorplanSidesheetExtent>();
  readonly toggleExtendedButtonClicked = output<void>();
  protected readonly kvm = computed(() => this.floorplanItem() as FloorplanKvm);
  protected readonly MonitoringCategory = MonitoringCategory;
  protected readonly SidesheetExtent = FloorplanSidesheetExtent;

  protected productItems!: MonitoringProductItem[];
  protected monitoringItems: MonitoringGroupedPartSourceItem[] = [];
  protected imageUrl?: string;
  protected fallbackImageUrl = "./assets/floorplan-menu-icons/kvm_menu.svg";
  protected monitoringTitle = getMonitoringConfigurationSummaryTitle(MonitoringCategory.KVMS);
  private readonly categoryLabels: KvmGroupedPartLabels = getKvmGroupedPartLabels();
  private readonly monitoringProductService = inject(MonitoringProductService);
  private readonly productService = inject(ProductService);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    toObservable(this.kvm)
      .pipe(
        switchMap((kvm) => {
          const productId = kvm.config.transmitterProductId;
          if (!productId) {
            return of(undefined);
          }

          return this.monitoringProductService
            .loadDeviceFromDB(productId, MonitoringCategory.KVMS, this.productService.getFilterCountry())
            .pipe(
              map((device) =>
                device?.productImageCantoID ? ProductImage.getCantoSourceUrl(device.productImageCantoID) : undefined,
              ),
              catchError(() => of(undefined)),
            );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((imageUrl) => {
        this.imageUrl = imageUrl;
      });

    effect(() => {
      const kvm = this.kvm();
      this.fallbackImageUrl = kvm.config.transmitterGroupImageCantoId
        ? ProductImage.getCantoSourceUrl(kvm.config.transmitterGroupImageCantoId)
        : "./assets/floorplan-menu-icons/kvm_menu.svg";
      this.productItems = kvm.config.getOptions();
      this.monitoringItems = this.buildMonitoringItems(kvm.config);
    });
  }

  private buildMonitoringItems(config: KvmConfiguration): MonitoringGroupedPartSourceItem[] {
    return buildKvmGroupedSourceItems(
      {
        transmitter: config.transmitter,
        receiver: config.receiver,
        accessories: config.accessories,
      },
      this.categoryLabels,
    );
  }
}
